<template>
  <div class="monarch-categories">
    <div class="header">
      <h1>Monarch Categories</h1>
      <div class="header-actions">
        <div class="filters">
          <label class="filter-checkbox">
            <input type="checkbox" v-model="hideDisabled" />
            Hide disabled
          </label>
          <label class="filter-checkbox">
            <input type="checkbox" v-model="budgetedOnly" />
            Budgeted only
          </label>
        </div>
        <button @click="syncCategories" :disabled="syncing" class="btn-sync">
          {{ syncing ? 'Syncing...' : 'Sync from Monarch' }}
        </button>
      </div>
    </div>

    <div v-if="loading" class="loading">Loading categories...</div>

    <div v-else-if="error" class="error-state">
      <p>{{ error }}</p>
      <button @click="loadCategories" class="btn-retry">Retry</button>
    </div>

    <div v-else-if="allCategories.length === 0" class="empty-state">
      <p>No categories found. Click "Sync from Monarch" to fetch.</p>
    </div>

    <div v-else>
      <p class="result-count">
        {{ groups.length }} groups, {{ filteredCategories.length }} categories
        <span v-if="hideDisabled || budgetedOnly" class="filter-note">
          ({{ allCategories.length }} total)
        </span>
      </p>

      <div v-for="group in groups" :key="group.id" class="group-section">
        <h2 class="group-header">
          {{ group.name }}
          <span :class="['group-type', `type-${group.type}`]">{{ group.type }}</span>
        </h2>
        <div class="categories-grid">
          <div
            v-for="cat in getCategoriesForGroup(group.id)"
            :key="cat.id"
            :class="['category-card', { disabled: cat.is_disabled }]"
          >
            <div class="cat-icon" v-if="cat.icon">{{ cat.icon }}</div>
            <div class="cat-info">
              <span class="cat-name">{{ cat.name }}</span>
              <span v-if="cat.is_system_category" class="cat-badge system">System</span>
              <span v-if="cat.is_disabled" class="cat-badge disabled">Disabled</span>
              <span v-if="cat.planned_amount" class="cat-badge budgeted">
                ${{ cat.planned_amount.toFixed(0) }}
              </span>
            </div>
            <div class="cat-id">{{ cat.monarch_category_id }}</div>
          </div>
        </div>
      </div>

      <div v-if="ungrouped.length > 0" class="group-section">
        <h2 class="group-header">Ungrouped</h2>
        <div class="categories-grid">
          <div
            v-for="cat in ungrouped"
            :key="cat.id"
            :class="['category-card', { disabled: cat.is_disabled }]"
          >
            <div class="cat-info">
              <span class="cat-name">{{ cat.name }}</span>
            </div>
            <div class="cat-id">{{ cat.monarch_category_id }}</div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, watch } from 'vue'
import { categoriesAPI } from '../services/api'

const allCategories = ref([])
const loading = ref(false)
const syncing = ref(false)
const error = ref(null)
const hideDisabled = ref(localStorage.getItem('categoriesHideDisabled') !== 'false')
const budgetedOnly = ref(localStorage.getItem('categoriesBudgetedOnly') === 'true')

watch(hideDisabled, (val) => localStorage.setItem('categoriesHideDisabled', val ? 'true' : 'false'))
watch(budgetedOnly, (val) => localStorage.setItem('categoriesBudgetedOnly', val ? 'true' : 'false'))

onMounted(() => {
  loadCategories()
})

const loadCategories = async () => {
  loading.value = true
  error.value = null
  try {
    const response = await categoriesAPI.getAll({ include_disabled: true })
    allCategories.value = response.data.categories || []
  } catch (err) {
    console.error('Error loading categories:', err)
    error.value = err.response?.data?.error || 'Failed to load categories'
  } finally {
    loading.value = false
  }
}

const syncCategories = async () => {
  syncing.value = true
  error.value = null
  try {
    await categoriesAPI.sync()
    await loadCategories()
  } catch (err) {
    console.error('Error syncing categories:', err)
    error.value = err.response?.data?.error || 'Failed to sync from Monarch'
  } finally {
    syncing.value = false
  }
}

const filteredCategories = computed(() => {
  let cats = allCategories.value
  if (hideDisabled.value) {
    cats = cats.filter(c => !c.is_disabled)
  }
  if (budgetedOnly.value) {
    cats = cats.filter(c => c.planned_amount && c.planned_amount > 0)
  }
  return cats
})

const groups = computed(() => {
  const groupMap = new Map()
  for (const cat of filteredCategories.value) {
    if (cat.group_id && cat.group_name) {
      if (!groupMap.has(cat.group_id)) {
        groupMap.set(cat.group_id, {
          id: cat.group_id,
          name: cat.group_name,
          type: cat.group_type || cat.category_type,
        })
      }
    }
  }
  return Array.from(groupMap.values())
})

const getCategoriesForGroup = (groupId) => {
  return filteredCategories.value
    .filter(c => c.group_id === groupId)
    .sort((a, b) => (a.order || 0) - (b.order || 0))
}

const ungrouped = computed(() => {
  const groupIds = new Set(groups.value.map(g => g.id))
  return filteredCategories.value.filter(c => !c.group_id || !groupIds.has(c.group_id))
})
</script>

<style scoped>
.monarch-categories {
  max-width: 1200px;
  margin: 0 auto;
}

.header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
  flex-wrap: wrap;
  gap: 1rem;
}

h1 { color: #2c3e50; margin: 0; }

.header-actions {
  display: flex;
  align-items: center;
  gap: 1rem;
  flex-wrap: wrap;
}

.filters {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.filter-checkbox {
  display: flex;
  align-items: center;
  gap: 0.4rem;
  font-size: 0.9rem;
  color: #2c3e50;
  cursor: pointer;
}

.btn-sync, .btn-retry {
  padding: 0.5rem 1rem;
  background: #27ae60;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 0.9rem;
  white-space: nowrap;
}
.btn-sync:hover:not(:disabled) { background: #219a52; }
.btn-sync:disabled { opacity: 0.6; cursor: not-allowed; }
.btn-retry { background: #3498db; margin-top: 1rem; }

.loading, .empty-state, .error-state {
  text-align: center;
  padding: 3rem;
  color: #7f8c8d;
}
.error-state { color: #c0392b; }

.result-count {
  color: #7f8c8d;
  font-size: 0.9rem;
  margin-bottom: 1.5rem;
}

.filter-note {
  font-style: italic;
}

.group-section {
  margin-bottom: 2rem;
}

.group-header {
  color: #2c3e50;
  font-size: 1.1rem;
  margin: 0 0 1rem 0;
  padding-bottom: 0.5rem;
  border-bottom: 2px solid #ecf0f1;
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.group-type {
  font-size: 0.75rem;
  padding: 0.2rem 0.5rem;
  border-radius: 4px;
  text-transform: uppercase;
  font-weight: 600;
}
.type-expense { background: #fde8e8; color: #c0392b; }
.type-income { background: #d4edda; color: #155724; }
.type-transfer { background: #d1ecf1; color: #0c5460; }

.categories-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
  gap: 0.75rem;
}

.category-card {
  background: white;
  padding: 0.75rem 1rem;
  border-radius: 6px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
  display: flex;
  align-items: center;
  gap: 0.5rem;
}
.category-card.disabled {
  opacity: 0.5;
}

.cat-icon {
  font-size: 1.2rem;
  flex-shrink: 0;
}

.cat-info {
  flex: 1;
  min-width: 0;
  display: flex;
  align-items: center;
  gap: 0.4rem;
  flex-wrap: wrap;
}

.cat-name {
  font-weight: 500;
  color: #2c3e50;
  font-size: 0.9rem;
}

.cat-badge {
  font-size: 0.65rem;
  padding: 0.1rem 0.35rem;
  border-radius: 3px;
  font-weight: 600;
  text-transform: uppercase;
}
.cat-badge.system { background: #e8f0fe; color: #3b82f6; }
.cat-badge.disabled { background: #f3f4f6; color: #9ca3af; }
.cat-badge.budgeted { background: #d4edda; color: #155724; text-transform: none; }

.cat-id {
  font-size: 0.7rem;
  color: #bdc3c7;
  font-family: monospace;
  flex-shrink: 0;
}
</style>
